<template>
  <div style="margin: 10px 15px">
    <el-form v-if="form" ref="form" :model="form" status-icon label-width="140px">
      <el-form-item label="用户名">
        <el-input v-model="form.userName" disabled></el-input>
      </el-form-item>
      <el-form-item label="头像">
        <el-upload
          class="avatar-uploader"
          :show-file-list="false"
          :action="uploadUrl"
          :headers="{ Authorization: 'Bearer ' + token }"
          :before-upload="uploadBefore"
          :on-progress="uploadProgress"
          :on-success="uploadSuccess"
          :on-error="uploadError"
          :on-remove="uploadRemove"
        >
          <img v-if="form.avatarUrl" :src="form.avatarUrl" class="avatar" />
          <i v-else class="el-icon-plus avatar-uploader-icon"></i>
        </el-upload>
      </el-form-item>
      <el-form-item label="手机号码" prop="mobile" :rules="mobileValidateRules">
        <el-input v-model="form.mobile" placeholder="请输入手机号码"></el-input>
        <small class="tips">手机号码可用于登录、找回密码等功能</small>
      </el-form-item>
      <el-form-item v-if="isMobileCode" label="短信验证码" prop="code" :rules="[{ required: true, message: '请输入短信验证码' }]">
        <el-input v-model="form.code" autocomplete="off" placeholder="请输入短信验证码">
          <template #append>
            <el-link :underline="false" @click.prevent="btnSendSmsClick">
              {{ countdown > 0 ? countdown + '秒' : '获取验证码' }}
            </el-link>
          </template>
        </el-input>
      </el-form-item>
    </el-form>

    <el-divider></el-divider>
    <div style="height: 10px"></div>

    <el-row>
      <el-col :span="24" align="center">
        <el-button type="primary" @click="btnSubmitClick">确 定</el-button>
      </el-col>
    </el-row>
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import utils from '@/utils';
import api from '@/api/profile';
import { defineComponent } from 'vue';
import { IUser } from '@/types/models';
import { IElForm } from '@/types/element';

interface IForm {
  userName: string;
  avatarUrl: string;
  mobile: string;
  code: string;
  mobileVerified: boolean;
}

interface IState {
  uploadUrl: string;
  uploadFileList: Array<{name: string, url: string}>;
  isSmsEnabled: boolean;
  isUserVerifyMobile: boolean;
  user: IUser;
  form: IForm;
  mobileValidateRules?: Array<Record<string, unknown>>;
  countdown: number;
}

export default defineComponent({
  data() {
    return {
      uploadUrl: '',
      uploadFileList: [],
      isSmsEnabled: false,
      isUserVerifyMobile: false,
      user: {} as IUser,
      form: {
        userName: '',
        avatarUrl: '',
        mobile: '',
        code: '',
        mobileVerified: false,
      },
      mobileValidateRules: undefined,
      countdown: 0,
    } as IState;
  },
  methods: {
    apiGet() {
      api.get((res) => {
        this.isSmsEnabled = res.isSmsEnabled;
        this.isUserVerifyMobile = res.isUserVerifyMobile;
        this.user = res.user;
        this.form = {
          userName: this.user.userName,
          avatarUrl: this.user.avatarUrl,
          mobile: this.user.mobile,
          code: '',
          mobileVerified: this.user.mobileVerified,
        };

        if (this.isUserVerifyMobile) {
          this.mobileValidateRules = [
            { required: true, message: '请输入手机号码' },
            {
              validator: utils.validateMobile,
              message: '请输入有效的手机号码',
            },
          ];
        } else {
          this.mobileValidateRules = [
            {
              validator: utils.validateMobile,
              message: '请输入有效的手机号码',
            },
          ];
        }

        if (this.form.avatarUrl) {
          this.uploadFileList.push({
            name: 'avatar',
            url: this.form.avatarUrl,
          });
        }
      });
    },

    apiSendSms() {
      api.sendSms({
        mobile: this.form.mobile,
      }, (res) => {
        if (!res) return;

        utils.notifySuccess('验证码发送成功，10分钟内有效');
        this.countdown = 60;
        const interval = setInterval(() => {
          this.countdown -= 1;
          if (this.countdown <= 0) {
            clearInterval(interval);
          }
        }, 1000);
      });
    },

    apiVerifyMobile() {
      api.verifyMobile({
        mobile: this.form.mobile,
        code: this.form.code,
      }, (res) => {
        if (!res) return;
        this.form.mobileVerified = true;
        this.apiSubmit();
      });
    },

    apiSubmit() {
      api.submit({
        userName: this.form.userName,
        avatarUrl: this.form.avatarUrl,
        mobile: this.form.mobile,
        mobileVerified: this.form.mobileVerified,
      }, (res) => {
        if (!res.value) return;
        utils.success('资料修改成功！');
      });
    },

    isMobile(value: string) {
      return /^1[3-9][0-9]\d{8}$/.test(value);
    },

    btnSendSmsClick() {
      if (this.countdown > 0) return;
      if (!this.form.mobile) {
        utils.notifyError('手机号码不能为空');
        return;
      }
      if (!this.isMobile(this.form.mobile)) {
        utils.notifyError('请输入有效的手机号码');
        return;
      }

      this.apiSendSms();
    },

    btnSubmitClick() {
      (this.$refs.form as IElForm).validate((valid) => {
        if (valid) {
          if (this.isMobileCode) {
            this.apiVerifyMobile();
          } else {
            this.apiSubmit();
          }
        }
      });
    },

    uploadBefore(file: {name: string; size: number}) {
      const re = /(\.jpg|\.jpeg|\.bmp|\.gif|\.png|\.webp)$/i;
      if (!re.exec(file.name)) {
        utils.error('头像只能是图片格式，请选择有效的文件上传!');
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        utils.error('头像图片大小不能超过 10MB!');
        return false;
      }
      return true;
    },

    uploadProgress() {
      utils.loading(true);
    },

    uploadSuccess(res: {value: string}, file: unknown, fileList: Array<string>) {
      this.form.avatarUrl = res.value;
      utils.loading(false);
      if (fileList.length > 1) fileList.splice(0, 1);
    },

    uploadError(err: {message: string}) {
      utils.loading(false);
      const error = JSON.parse(err.message);
      utils.error(error.message);
    },

    uploadRemove() {
      this.form.avatarUrl = '';
    },
  },
  computed: {
    ...mapGetters(['token']),

    isMobileCode(): boolean {
      if (this.isUserVerifyMobile) {
        return !this.user.mobileVerified || this.user.mobile !== this.form.mobile;
      }
      if (this.isSmsEnabled) {
        return this.user.mobile !== this.form.mobile;
      }
      return false;
    },
  },
  created() {
    this.uploadUrl = api.getUploadUrl();
    this.apiGet();
  },
});
</script>

<style scoped>
.avatar-uploader {
  width: 178px;
}
.avatar-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.avatar-uploader .el-upload:hover {
  border-color: #409eff;
}
.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  line-height: 178px;
  text-align: center;
}
.avatar {
  width: 178px;
  height: 178px;
  display: block;
}
</style>
